import { EventType, type AGUIEvent } from '@ag-ui/core'
import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  type AppendMessage,
  type Attachment,
  type ThreadMessage,
} from '@assistant-ui/react'
import { HubConnectionState, type HubConnection } from '@microsoft/signalr'
import { useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import {
  createDmpChatCancellationMessage,
  createDmpChatConnection,
  createDmpChatTaskBody,
  DMP_CHAT_CLIENT_TYPE,
} from '../dmpChatTransport'
import { createDmpChatSignalRTraceRecorder, downloadDmpChatSignalRTrace } from '../dmpChatSignalRTrace'
import { DMP_CHAT_SIGNALR_TRACE_ENABLED } from '../dmpChatSignalRTraceConfig'
import { createDmpChatTaskAdapter, type DmpChatTaskAdapter } from '../dmpChatTasks'
import {
  createDmpChatAttachmentAdapter,
  type DmpChatAttachmentUploadPath,
  type DmpChatUploadResponse,
} from '../dmpChatAttachments'
import { createDmpChatRetryMessage, isDmpChatPrompt } from '../dmpChatMessageUtils'
import { clearDmpChatLastActive } from '../dmpChatLastActive'
import { useDmpChatToolDisplayMode, type DmpChatToolDisplayMode } from '../dmpChatToolDisplay'
import type { DmpChatSessionFile } from '../dmpChatSessionFiles'
import { useDmpChatAgent } from './DmpChatAgentContext'
import {
  createDmpChatCancellationEvent,
  createDmpChatMockFixture,
  DMP_CHAT_FIXTURE_DELAY_MS,
  type DmpChatMockScenario,
} from '../fixtures/dmpChatFixtures'
import { DMP_CHAT_MOCK_ENABLED } from '../dmpChatMockConfig'
import {
  createDmpChatState,
  createDmpChatId,
  createDmpChatTurn,
  DMP_CHAT_CONNECTION_FAILURE_SUMMARY,
  DMP_CHAT_TASK_FAILURE_SUMMARY,
  getActiveDmpChatTurn,
  isDmpChatTurnActive,
  parseDmpChatEvent,
  projectDmpChatMessages,
  reduceDmpChatState,
  type DmpChatHistorySession,
  type DmpChatAttachment,
  type DmpChatTaskStatus,
  type DmpChatTerminalOutcome,
  type DmpChatTurn,
  type DmpChatUsage,
} from '../state/dmpChatState'

type DmpChatConnectionStatus = 'connecting' | 'connected' | 'mock' | 'reconnecting' | 'disconnected' | 'failed'
export type DmpChatSidebarTab = 'files'

export interface DmpChatSidebarTabRequest {
  tab: DmpChatSidebarTab
  requestId: number
}

interface TurnOperation {
  turnId: string
  cancellationRequested: boolean
  cancellationSent: boolean
}

export interface DmpChatRuntimeContextValue {
  activeTurn: DmpChatTurn | null
  chatHistoryId: string
  terminalOutcome: DmpChatTerminalOutcome | null
  lastUsage: DmpChatUsage | null
  isTurnActive: boolean
  isHydrating: boolean
  prepareResume: () => boolean
  resumeHistory: (session: DmpChatHistorySession) => boolean
  cancelResume: () => void
  newChat: () => boolean
  canRetry: boolean
  retryLastTurn: () => void
  toolDisplayMode: DmpChatToolDisplayMode
  setToolDisplayMode: (mode: DmpChatToolDisplayMode) => void
  connectionStatus: DmpChatConnectionStatus
  connectionError: string | null
  isRecovering: boolean
  signalRTraceEnabled: boolean
  signalRTraceMessageCount: number
  downloadSignalRTrace: () => void
  mockModeEnabled: boolean
  mockScenario: DmpChatMockScenario
  setMockScenario: (scenario: DmpChatMockScenario) => void
  requestSidebarTab: (tab: DmpChatSidebarTab) => void
  sidebarTabRequest: DmpChatSidebarTabRequest | null
  sessionFiles: readonly DmpChatSessionFile[]
  sessionFilesLoading: boolean
  sessionFilesFetching: boolean
  sessionFilesError: boolean
  refetchSessionFiles: () => void
  sessionFilesRefreshVersion: number
}

const DmpChatRuntimeContext = createContext<DmpChatRuntimeContextValue | null>(null)
const DMP_CHAT_EVENTS_PER_FRAME = 100
const DMP_CHAT_TASK_POLL_INTERVAL_MS = 2_000
const DMP_CHAT_TERMINAL_TASK_STATUSES = new Set<DmpChatTaskStatus>(['completed', 'failed', 'cancelled', 'timed-out'])
const DMP_CHAT_DEFAULT_MOCK_SCENARIO: DmpChatMockScenario = 'success'

type DmpChatRuntimeProviderProps = PropsWithChildren<{
  projectId: number
  organisationId: number | undefined
}>

export function DmpChatRuntimeProvider({ children, projectId, organisationId }: DmpChatRuntimeProviderProps) {
  const [token] = useSessionStorage('token', '')
  const { agentKey, agents, agentsLoading, selectAgentByKey } = useDmpChatAgent()
  const restClient = useRestClient()
  const taskAdapter = useMemo<DmpChatTaskAdapter>(() => createDmpChatTaskAdapter(restClient), [restClient])
  const [state, dispatch] = useReducer(reduceDmpChatState, undefined, () => createDmpChatState(projectId))
  const [connectionStatus, setConnectionStatus] = useState<DmpChatConnectionStatus>(
    DMP_CHAT_MOCK_ENABLED ? 'mock' : token ? 'disconnected' : 'failed'
  )
  const [connectionError, setConnectionError] = useState<string | null>(
    DMP_CHAT_MOCK_ENABLED || token ? null : 'Authentication is required.'
  )
  const [isRecovering, setIsRecovering] = useState(false)
  const [isHydrating, setIsHydrating] = useState(false)
  const [pendingResumeAgentKey, setPendingResumeAgentKey] = useState<string | null>(null)
  const { mode: toolDisplayMode, setMode: setToolDisplayMode } = useDmpChatToolDisplayMode()
  const [signalRTraceRecorder] = useState(() => createDmpChatSignalRTraceRecorder(DMP_CHAT_SIGNALR_TRACE_ENABLED))
  const [signalRTraceMessageCount, setSignalRTraceMessageCount] = useState(0)
  const [mockScenario, setMockScenario] = useState<DmpChatMockScenario>(DMP_CHAT_DEFAULT_MOCK_SCENARIO)
  const [sessionFilesRefreshVersion, setSessionFilesRefreshVersion] = useState(0)
  const [sidebarTabRequest, setSidebarTabRequest] = useState<DmpChatSidebarTabRequest | null>(null)
  const connectionRef = useRef<HubConnection | null>(null)
  const startPromiseRef = useRef<Promise<void> | null>(null)
  const stopPromiseRef = useRef<Promise<void> | null>(null)
  const subscribeReceiveMessageRef = useRef<(() => void) | null>(null)
  const unsubscribeReceiveMessageRef = useRef<(() => void) | null>(null)
  const joinedChatIdRef = useRef<string | null>(null)
  const activeTurnRef = useRef<DmpChatTurn | null>(null)
  const turnOperationRef = useRef<TurnOperation | null>(null)
  const signalRTraceMessageCountRef = useRef(0)
  const mountedRef = useRef(true)
  const eventQueueRef = useRef<AGUIEvent[]>([])
  const eventFlushFrameRef = useRef<number | null>(null)
  const mockTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const clearComposerAttachmentsRef = useRef<(() => Promise<void>) | null>(null)
  const activeTurn = getActiveDmpChatTurn(state)
  const isTurnActive = isDmpChatTurnActive(activeTurn)
  const lastUsage = state.thread.lastUsage
  const messages = projectDmpChatMessages(state.thread, toolDisplayMode)
  const threadProjectId = state.thread.projectId
  const chatHistoryId = state.thread.chatHistoryId
  const canListSessionFiles = !DMP_CHAT_MOCK_ENABLED && organisationId != null && chatHistoryId.trim().length > 0
  const sessionFilesQuery = restClient.getChatHistoryApiListSessionFiles.useQuery(
    {
      path: {
        organisationId: organisationId ?? 0,
        projectId: threadProjectId,
        chatHistoryId,
      },
    },
    {
      enabled: canListSessionFiles,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    }
  )
  const { refetch: refetchSessionFilesQuery } = sessionFilesQuery
  const sessionFiles = useMemo<readonly DmpChatSessionFile[]>(
    () => (canListSessionFiles ? (sessionFilesQuery.data ?? []) : []),
    [canListSessionFiles, sessionFilesQuery.data]
  )
  const wasTurnActiveForSessionFilesRef = useRef(isTurnActive)
  const previousSessionFilesRefreshVersionRef = useRef(sessionFilesRefreshVersion)
  const attachmentAdapter = useMemo(
    () =>
      createDmpChatAttachmentAdapter({
        chatHistoryId,
        mockMode: DMP_CHAT_MOCK_ENABLED,
        organisationId,
        projectId: threadProjectId,
        upload: async (path: DmpChatAttachmentUploadPath, body: FormData): Promise<DmpChatUploadResponse> => {
          const result = await restClient.postChatHistoryApiUploadSessionFiles({
            body,
            parameters: { path },
          } as Parameters<typeof restClient.postChatHistoryApiUploadSessionFiles>[0])
          return result as DmpChatUploadResponse
        },
        onUploadSuccess: () => setSessionFilesRefreshVersion((version) => version + 1),
      }),
    [chatHistoryId, organisationId, restClient, threadProjectId]
  )
  const activeTurnId = activeTurn?.id
  const activeTaskId = activeTurn?.task.id
  const activeTaskStatus = activeTurn?.task.status

  useEffect(() => {
    const wasTurnActive = wasTurnActiveForSessionFilesRef.current
    wasTurnActiveForSessionFilesRef.current = isTurnActive
    if (wasTurnActive && !isTurnActive && canListSessionFiles) {
      void refetchSessionFilesQuery()
    }
  }, [canListSessionFiles, isTurnActive, refetchSessionFilesQuery])

  useEffect(() => {
    const previousRefreshVersion = previousSessionFilesRefreshVersionRef.current
    previousSessionFilesRefreshVersionRef.current = sessionFilesRefreshVersion
    if (previousRefreshVersion !== sessionFilesRefreshVersion && canListSessionFiles) {
      void refetchSessionFilesQuery()
    }
  }, [canListSessionFiles, refetchSessionFilesQuery, sessionFilesRefreshVersion])

  const requestSidebarTab = useCallback((tab: DmpChatSidebarTab) => {
    setSidebarTabRequest((currentRequest) => ({
      tab,
      requestId: (currentRequest?.requestId ?? 0) + 1,
    }))
  }, [])
  const refetchSessionFiles = useCallback(() => {
    void refetchSessionFilesQuery()
  }, [refetchSessionFilesQuery])

  const prepareResume = useCallback(() => {
    if (isHydrating || getActiveDmpChatTurn(state)) {
      return false
    }

    setIsHydrating(true)
    return true
  }, [isHydrating, state])

  const cancelResume = useCallback(() => {
    setPendingResumeAgentKey(null)
    setIsHydrating(false)
  }, [])

  const resumeHistory = useCallback(
    (session: DmpChatHistorySession) => {
      if (
        getActiveDmpChatTurn(state) ||
        session.deleted ||
        typeof session.chatHistoryId !== 'string' ||
        !session.chatHistoryId.trim()
      ) {
        cancelResume()
        return false
      }

      void clearComposerAttachmentsRef.current?.()
      setIsHydrating(true)
      dispatch({ type: 'hydrate-history', session })
      const savedAgentKey = session.agentKey?.trim()
      if (!savedAgentKey) {
        setPendingResumeAgentKey(null)
        setIsHydrating(false)
        return true
      }

      if (agentsLoading) {
        setPendingResumeAgentKey(savedAgentKey)
        return true
      }

      selectAgentByKey(savedAgentKey)
      setPendingResumeAgentKey(null)
      setIsHydrating(false)
      return true
    },
    [agentsLoading, cancelResume, selectAgentByKey, state]
  )

  useEffect(() => {
    if (!pendingResumeAgentKey || agentsLoading) {
      return
    }

    selectAgentByKey(pendingResumeAgentKey)
    setPendingResumeAgentKey(null)
    setIsHydrating(false)
  }, [agents, agentsLoading, pendingResumeAgentKey, selectAgentByKey])

  const readTaskRecovery = useCallback(
    async (
      taskId: string
    ): Promise<{ status: DmpChatTaskStatus | null; message: string | null; usage: DmpChatUsage | null }> => {
      const status = await taskAdapter.readStatus(taskId)
      if (!status || !DMP_CHAT_TERMINAL_TASK_STATUSES.has(status)) {
        return { status, message: null, usage: null }
      }

      const result = await taskAdapter.readResult(taskId)
      return {
        status: result?.status ?? status,
        message: result?.message ?? null,
        usage: result?.usage ?? null,
      }
    },
    [taskAdapter]
  )
  const downloadSignalRTrace = useCallback(() => {
    downloadDmpChatSignalRTrace(signalRTraceRecorder.getEntries())
  }, [signalRTraceRecorder])
  const clearMockTimers = useCallback(() => {
    for (const timer of mockTimersRef.current) {
      clearTimeout(timer)
    }
    mockTimersRef.current = []
  }, [])
  const newChat = useCallback(() => {
    if (getActiveDmpChatTurn(state) || isDmpChatTurnActive(activeTurnRef.current)) {
      return false
    }

    clearMockTimers()
    setPendingResumeAgentKey(null)
    setIsHydrating(false)
    setConnectionError(null)
    void clearComposerAttachmentsRef.current?.()
    clearDmpChatLastActive(projectId)
    dispatch({ type: 'new-chat' })
    return true
  }, [clearMockTimers, projectId, state])
  const playMockEvents = useCallback(
    (rawEvents: readonly string[]) => {
      clearMockTimers()
      rawEvents.forEach((rawEvent, index) => {
        const timer = setTimeout(() => {
          if (mountedRef.current) {
            dispatch({ type: 'ingest-raw-event', rawEvent })
          }
        }, index * DMP_CHAT_FIXTURE_DELAY_MS)
        mockTimersRef.current.push(timer)
      })
    },
    [clearMockTimers]
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => () => clearMockTimers(), [clearMockTimers])

  const startConnection = useCallback((connection: HubConnection): Promise<void> => {
    if (stopPromiseRef.current) {
      return stopPromiseRef.current.then(() => startConnection(connection))
    }

    if (connection.state === HubConnectionState.Connected) {
      subscribeReceiveMessageRef.current?.()
      return Promise.resolve()
    }

    if (startPromiseRef.current) {
      return startPromiseRef.current
    }

    if (connection.state === HubConnectionState.Reconnecting) {
      return Promise.reject(new Error('The CollaborationHub connection is reconnecting.'))
    }

    if (connection.state !== HubConnectionState.Disconnected) {
      return Promise.reject(new Error('The CollaborationHub connection is not ready to start.'))
    }

    setConnectionStatus('connecting')
    const startPromise = (async () => {
      await connection.start()
      if (connectionRef.current === connection) {
        setConnectionStatus('connected')
        setConnectionError(null)
        subscribeReceiveMessageRef.current?.()
      }
    })().catch((error) => {
      if (connectionRef.current === connection) {
        setConnectionStatus('failed')
        setConnectionError('The Project Agent connection could not be established.')
      }
      throw error
    })

    startPromiseRef.current = startPromise
    startPromise.then(
      () => {
        if (startPromiseRef.current === startPromise) {
          startPromiseRef.current = null
        }
      },
      () => {
        if (startPromiseRef.current === startPromise) {
          startPromiseRef.current = null
        }
      }
    )
    return startPromise
  }, [])

  const ensureConnected = useCallback(async (): Promise<HubConnection> => {
    const connection = connectionRef.current
    if (!connection) {
      throw new Error('The CollaborationHub connection is unavailable.')
    }

    await startConnection(connection)
    if (connection.state !== HubConnectionState.Connected) {
      throw new Error('The CollaborationHub connection is not connected.')
    }

    return connection
  }, [startConnection])

  const joinChatChannel = useCallback(async (connection: HubConnection, chatId: string): Promise<void> => {
    if (joinedChatIdRef.current === chatId) {
      return
    }

    await connection.invoke('JoinChatChannel', chatId, DMP_CHAT_CLIENT_TYPE)
    if (connectionRef.current === connection) {
      joinedChatIdRef.current = chatId
    }
  }, [])

  useEffect(() => {
    activeTurnRef.current = activeTurn
    if (!activeTurn) {
      turnOperationRef.current = null
    }
  }, [activeTurn])

  useEffect(() => {
    if (
      DMP_CHAT_MOCK_ENABLED ||
      !activeTurnId ||
      !activeTaskId ||
      activeTaskStatus === undefined ||
      activeTaskStatus === 'creating'
    ) {
      return
    }

    let isActive = true
    let pollTimer: ReturnType<typeof setTimeout> | null = null

    const pollTask = async () => {
      if (!isActive || !mountedRef.current || activeTurnRef.current?.id !== activeTurnId) {
        return
      }

      try {
        const recovery = await readTaskRecovery(activeTaskId)
        const recoveredStatus = recovery.status
        if (!isActive || !mountedRef.current || activeTurnRef.current?.id !== activeTurnId) {
          return
        }

        if (
          recoveredStatus === 'completed' ||
          recoveredStatus === 'failed' ||
          recoveredStatus === 'cancelled' ||
          recoveredStatus === 'timed-out'
        ) {
          dispatch({
            type: 'reconcile-task',
            turnId: activeTurnId,
            taskId: activeTaskId,
            status: recoveredStatus,
            message: recovery.message,
            usage: recovery.usage,
          })
          return
        }
      } catch {
        // The live channel remains the primary path; retry transient Tasks API
        // failures without changing the visible Turn outcome.
      }

      if (isActive && mountedRef.current && activeTurnRef.current?.id === activeTurnId) {
        pollTimer = setTimeout(() => {
          void pollTask()
        }, DMP_CHAT_TASK_POLL_INTERVAL_MS)
      }
    }

    pollTimer = setTimeout(() => {
      void pollTask()
    }, DMP_CHAT_TASK_POLL_INTERVAL_MS)

    return () => {
      isActive = false
      if (pollTimer !== null) {
        clearTimeout(pollTimer)
      }
    }
  }, [activeTaskId, activeTaskStatus, activeTurnId, readTaskRecovery])

  useEffect(() => {
    if (DMP_CHAT_MOCK_ENABLED) {
      connectionRef.current = null
      setConnectionStatus('mock')
      setConnectionError(null)
      return
    }

    const connection = token ? createDmpChatConnection(token) : null
    connectionRef.current = connection

    if (!connection) {
      setConnectionStatus('failed')
      setConnectionError('Authentication is required.')
      return () => {
        connectionRef.current = null
        startPromiseRef.current = null
        stopPromiseRef.current = null
        subscribeReceiveMessageRef.current = null
        unsubscribeReceiveMessageRef.current = null
        joinedChatIdRef.current = null
      }
    }

    let isActive = true
    const failActiveTurn = () => {
      const turn = activeTurnRef.current
      if (!turn) {
        return
      }

      dispatch({
        type: 'finish-turn',
        turnId: turn.id,
        outcome: {
          type: 'failure',
          summary: DMP_CHAT_CONNECTION_FAILURE_SUMMARY,
        },
      })
    }
    const recoverTaskResult = async (turn: DmpChatTurn) => {
      try {
        const recovery = await readTaskRecovery(turn.task.id)
        if (!isActive || activeTurnRef.current?.id !== turn.id || !recovery.status) {
          return
        }

        dispatch({
          type: 'reconcile-task',
          turnId: turn.id,
          taskId: turn.task.id,
          status: recovery.status,
          message: recovery.message,
          usage: recovery.usage,
        })
      } catch {
        if (isActive && activeTurnRef.current?.id === turn.id) {
          setConnectionError('The Project Agent task result could not be checked. Try again.')
        }
      }
    }
    const recoverAfterConnectionClose = async (turn: DmpChatTurn) => {
      try {
        const recovery = await readTaskRecovery(turn.task.id)
        const recoveredStatus = recovery.status
        if (!isActive || activeTurnRef.current?.id !== turn.id) {
          return
        }

        if (
          recoveredStatus === 'completed' ||
          recoveredStatus === 'failed' ||
          recoveredStatus === 'cancelled' ||
          recoveredStatus === 'timed-out'
        ) {
          setConnectionError(null)
          dispatch({
            type: 'reconcile-task',
            turnId: turn.id,
            taskId: turn.task.id,
            status: recoveredStatus,
            message: recovery.message,
            usage: recovery.usage,
          })
          return
        }

        failActiveTurn()
      } catch {
        if (isActive && activeTurnRef.current?.id === turn.id) {
          setConnectionError('The Project Agent request could not be recovered. Try again.')
          failActiveTurn()
        }
      }
    }
    const recoverActiveTurn = async (turn: DmpChatTurn) => {
      setIsRecovering(true)
      let cancellationSendFailed = false
      try {
        await joinChatChannel(connection, turn.chatId)
        if (!isActive || activeTurnRef.current?.id !== turn.id) {
          return
        }

        if (turn.task.status === 'creating') {
          return
        }

        const operation = turnOperationRef.current
        if (operation?.turnId === turn.id && operation.cancellationRequested && !operation.cancellationSent) {
          try {
            operation.cancellationSent = true
            await sendCancellation(connection, turn)
          } catch {
            operation.cancellationSent = false
            cancellationSendFailed = true
            setConnectionError('The cancellation could not be sent. The request is still active; try again.')
          }
        }

        const recovery = await readTaskRecovery(turn.task.id)
        const recoveredStatus = recovery.status
        if (!isActive || activeTurnRef.current?.id !== turn.id) {
          return
        }

        if (!recoveredStatus) {
          if (cancellationSendFailed) {
            return
          }

          setConnectionError('The Project Agent request could not be recovered. Try again.')
          failActiveTurn()
          return
        }

        dispatch({
          type: 'reconcile-task',
          turnId: turn.id,
          taskId: turn.task.id,
          status: recoveredStatus,
          message: recovery.message,
          usage: recovery.usage,
        })
      } catch {
        if (cancellationSendFailed) {
          if (isActive) {
            setConnectionError('The cancellation could not be sent. The request is still active; try again.')
          }
          return
        }

        if (isActive) {
          setConnectionStatus('failed')
          setConnectionError('The Project Agent request could not be recovered. Try again.')
          failActiveTurn()
        }
      } finally {
        if (isActive) {
          setIsRecovering(false)
        }
      }
    }
    const flushEvents = () => {
      eventFlushFrameRef.current = null
      if (!isActive) {
        eventQueueRef.current = []
        return
      }

      const events = eventQueueRef.current.splice(0, DMP_CHAT_EVENTS_PER_FRAME)
      const turn = activeTurnRef.current
      const operation = turnOperationRef.current
      const cancellationIsPending =
        turn?.status === 'cancellation-requested' ||
        (turn != null && operation?.turnId === turn.id && operation.cancellationRequested)

      for (const event of events) {
        dispatch({ type: 'ingest-event', event })
      }

      if (events.some((event) => event.type === EventType.RUN_FINISHED) && turn && cancellationIsPending) {
        void recoverTaskResult(turn)
      }

      if (eventQueueRef.current.length > 0) {
        eventFlushFrameRef.current = window.requestAnimationFrame(flushEvents)
      }
    }
    const handleReceiveMessage = (rawEvent: string) => {
      if (!isActive) {
        return
      }

      const event = parseDmpChatEvent(rawEvent)
      const traceMessageCount = signalRTraceRecorder.record(rawEvent, event)
      if (traceMessageCount !== signalRTraceMessageCountRef.current) {
        signalRTraceMessageCountRef.current = traceMessageCount
        setSignalRTraceMessageCount(traceMessageCount)
      }

      if (!event) {
        return
      }

      eventQueueRef.current.push(event)
      if (eventFlushFrameRef.current === null) {
        eventFlushFrameRef.current = window.requestAnimationFrame(flushEvents)
      }
    }

    const subscribeReceiveMessage = () => {
      if (isActive && !receiveMessageSubscribed) {
        connection.on('ReceiveMessage', handleReceiveMessage)
        receiveMessageSubscribed = true
      }
    }
    const unsubscribeReceiveMessage = () => {
      if (receiveMessageSubscribed) {
        connection.off('ReceiveMessage', handleReceiveMessage)
        receiveMessageSubscribed = false
      }
    }
    let receiveMessageSubscribed = false
    subscribeReceiveMessageRef.current = subscribeReceiveMessage
    unsubscribeReceiveMessageRef.current = unsubscribeReceiveMessage
    subscribeReceiveMessage()
    connection.onreconnecting(() => {
      joinedChatIdRef.current = null
      if (isActive) {
        setConnectionStatus('reconnecting')
        setConnectionError(null)
      }
    })
    connection.onreconnected(async () => {
      if (!isActive) {
        return
      }

      setConnectionStatus('connected')
      setConnectionError(null)
      const turn = activeTurnRef.current
      if (!turn) {
        return
      }

      void recoverActiveTurn(turn)
    })
    connection.onclose(() => {
      joinedChatIdRef.current = null
      if (isActive) {
        setConnectionStatus('disconnected')
        setConnectionError('The Project Agent connection was closed. Try again.')
        const turn = activeTurnRef.current
        if (turn) {
          void recoverAfterConnectionClose(turn)
        }
      }
    })

    return () => {
      isActive = false
      if (eventFlushFrameRef.current !== null) {
        window.cancelAnimationFrame(eventFlushFrameRef.current)
        eventFlushFrameRef.current = null
      }
      eventQueueRef.current = []
      unsubscribeReceiveMessage()
      subscribeReceiveMessageRef.current = null
      unsubscribeReceiveMessageRef.current = null
      joinedChatIdRef.current = null
      if (connectionRef.current === connection) {
        connectionRef.current = null
      }
      startPromiseRef.current = null
      stopPromiseRef.current = null
      if (connection.state !== HubConnectionState.Disconnected) {
        void connection.stop().catch(() => undefined)
      }
    }
  }, [joinChatChannel, readTaskRecovery, signalRTraceRecorder, startConnection, taskAdapter, token])

  const handleNew = useCallback(
    async (message: AppendMessage) => {
      const prompt = extractText(message)
      if (!isDmpChatPrompt(prompt) || isHydrating || isTurnActive || activeTurnRef.current) {
        return
      }

      const messageAttachments = message.attachments ?? []
      if (messageAttachments.some((attachment) => attachment.status.type !== 'complete')) {
        setConnectionError('Attachments are still uploading or have failed. Remove them or wait before sending.')
        return
      }

      const attachments = extractDmpChatAttachments(messageAttachments)
      if (attachments.length !== messageAttachments.length) {
        setConnectionError('The attached files are not ready to send. Remove them and try again.')
        return
      }

      if (threadProjectId !== projectId || (!DMP_CHAT_MOCK_ENABLED && organisationId == null)) {
        setConnectionError('The current project is not available for DMP Chat.')
        return
      }

      setConnectionError(null)
      const turn = createDmpChatTurn(state.thread, prompt, createDmpChatId, attachments)
      const operation: TurnOperation = {
        turnId: turn.id,
        cancellationRequested: false,
        cancellationSent: false,
      }
      turnOperationRef.current = operation
      activeTurnRef.current = turn
      dispatch({ type: 'submit-turn', turn })

      if (DMP_CHAT_MOCK_ENABLED) {
        playMockEvents(createDmpChatMockFixture(mockScenario, turn))
        return
      }

      if (organisationId == null) {
        return
      }

      let taskCreationStarted = false
      try {
        const connection = await ensureConnected()
        await joinChatChannel(connection, turn.chatId)
        if (!mountedRef.current || activeTurnRef.current?.id !== turn.id) {
          return
        }
        setConnectionError(null)

        taskCreationStarted = true
        const task = await taskAdapter.create(
          createDmpChatTaskBody(projectId, organisationId, turn, agentKey),
          turn.requestId
        )
        if (!mountedRef.current || activeTurnRef.current?.id !== turn.id) {
          return
        }

        dispatch({
          type: 'task-accepted',
          turnId: turn.id,
          task,
        })

        if (operation.cancellationRequested && connection.state === HubConnectionState.Connected) {
          try {
            operation.cancellationSent = true
            await sendCancellation(connection, turn)
          } catch {
            operation.cancellationSent = false
            setConnectionError('The cancellation could not be sent. The request is still active; try again.')
          }
        }
      } catch {
        if (!mountedRef.current || activeTurnRef.current?.id !== turn.id) {
          return
        }

        setConnectionError(
          taskCreationStarted
            ? 'The Project Agent request could not be queued. Try again.'
            : 'The Project Agent thread could not be joined. Try again.'
        )
        dispatch({
          type: 'finish-turn',
          turnId: turn.id,
          outcome: {
            type: 'failure',
            summary: taskCreationStarted ? DMP_CHAT_TASK_FAILURE_SUMMARY : DMP_CHAT_CONNECTION_FAILURE_SUMMARY,
          },
        })
      }
    },
    [
      agentKey,
      ensureConnected,
      isHydrating,
      isTurnActive,
      joinChatChannel,
      mockScenario,
      organisationId,
      playMockEvents,
      projectId,
      state.thread,
      taskAdapter,
      threadProjectId,
    ]
  )

  const handleCancel = useCallback(async () => {
    if (!mountedRef.current) {
      return
    }

    const turn = getActiveDmpChatTurn(state)
    if (!turn || !isDmpChatTurnActive(turn)) {
      return
    }

    const operation = turnOperationRef.current
    if (operation?.turnId === turn.id) {
      operation.cancellationRequested = true
    }

    dispatch({ type: 'request-cancellation', turnId: turn.id })
    if (DMP_CHAT_MOCK_ENABLED) {
      if (operation?.turnId === turn.id && operation.cancellationSent) {
        return
      }

      if (operation?.turnId === turn.id) {
        operation.cancellationSent = true
      }
      playMockEvents([createDmpChatCancellationEvent(turn)])
      return
    }

    if (turn.task.status === 'creating') {
      return
    }

    const connection = connectionRef.current
    if (!connection || connection.state !== HubConnectionState.Connected || joinedChatIdRef.current !== turn.chatId) {
      setConnectionError('The cancellation will be sent when the Project Agent connection recovers.')
      return
    }

    if (operation?.turnId === turn.id && operation.cancellationSent) {
      return
    }

    try {
      if (operation?.turnId === turn.id) {
        operation.cancellationSent = true
      }
      await sendCancellation(connection, turn)
      if (!mountedRef.current || activeTurnRef.current?.id !== turn.id) {
        return
      }
    } catch {
      if (!mountedRef.current || activeTurnRef.current?.id !== turn.id) {
        return
      }

      if (operation?.turnId === turn.id) {
        operation.cancellationSent = false
      }
      setConnectionError('The cancellation could not be sent. The request is still active; try again.')
    }
  }, [playMockEvents, state])

  const latestTurn = state.thread.turns[state.thread.turns.length - 1] ?? null
  const canRetry =
    !isTurnActive &&
    latestTurn != null &&
    latestTurn.prompt.trim().length > 0 &&
    (latestTurn.status === 'failed' || latestTurn.status === 'cancelled')
  const retryLastTurn = useCallback(() => {
    if (!canRetry || !latestTurn) {
      return
    }

    void handleNew(createDmpChatRetryMessage(latestTurn))
  }, [canRetry, handleNew, latestTurn])

  const runtime = useExternalStoreRuntime<ThreadMessage>({
    messages,
    isRunning: isTurnActive,
    isSendDisabled: isTurnActive || isHydrating,
    onNew: handleNew,
    onCancel: handleCancel,
    adapters: {
      attachments: attachmentAdapter,
    },
  })

  useEffect(() => {
    clearComposerAttachmentsRef.current = () => runtime.thread.composer.clearAttachments()
    return () => {
      clearComposerAttachmentsRef.current = null
    }
  }, [runtime])

  const contextValue = useMemo<DmpChatRuntimeContextValue>(
    () => ({
      activeTurn,
      chatHistoryId,
      terminalOutcome: state.thread.lastOutcome,
      lastUsage,
      isTurnActive,
      isHydrating,
      prepareResume,
      resumeHistory,
      cancelResume,
      newChat,
      canRetry,
      retryLastTurn,
      setToolDisplayMode,
      toolDisplayMode,
      connectionStatus,
      connectionError,
      isRecovering,
      signalRTraceEnabled: DMP_CHAT_SIGNALR_TRACE_ENABLED,
      signalRTraceMessageCount,
      downloadSignalRTrace,
      mockModeEnabled: DMP_CHAT_MOCK_ENABLED,
      mockScenario,
      setMockScenario,
      requestSidebarTab,
      sidebarTabRequest,
      sessionFiles,
      sessionFilesLoading: canListSessionFiles && sessionFilesQuery.isLoading,
      sessionFilesFetching: canListSessionFiles && sessionFilesQuery.isFetching,
      sessionFilesError: canListSessionFiles && sessionFilesQuery.isError,
      refetchSessionFiles,
      sessionFilesRefreshVersion,
    }),
    [
      activeTurn,
      canRetry,
      chatHistoryId,
      connectionError,
      connectionStatus,
      canListSessionFiles,
      isRecovering,
      isHydrating,
      isTurnActive,
      lastUsage,
      cancelResume,
      newChat,
      retryLastTurn,
      prepareResume,
      resumeHistory,
      signalRTraceMessageCount,
      downloadSignalRTrace,
      mockScenario,
      requestSidebarTab,
      refetchSessionFiles,
      sessionFiles,
      sessionFilesQuery.isError,
      sessionFilesQuery.isFetching,
      sessionFilesQuery.isLoading,
      sidebarTabRequest,
      setMockScenario,
      sessionFilesRefreshVersion,
      setToolDisplayMode,
      state.thread.lastOutcome,
      toolDisplayMode,
    ]
  )

  return (
    <DmpChatRuntimeContext.Provider value={contextValue}>
      <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>
    </DmpChatRuntimeContext.Provider>
  )
}

export function useDmpChatRuntime(): DmpChatRuntimeContextValue {
  const context = useContext(DmpChatRuntimeContext)
  if (!context) {
    throw new Error('useDmpChatRuntime must be used inside DmpChatRuntimeProvider')
  }

  return context
}

function extractText(message: AppendMessage): string {
  return message.content.map((part) => (part.type === 'text' ? part.text : '')).join('')
}

function extractDmpChatAttachments(attachments: readonly Attachment[]): DmpChatAttachment[] {
  return attachments.flatMap((attachment) => {
    const attachmentWithMetadata = attachment as Attachment & {
      inputRef?: unknown
      kind?: unknown
      sizeBytes?: unknown
    }
    const inputRef =
      typeof attachmentWithMetadata.inputRef === 'string'
        ? attachmentWithMetadata.inputRef.trim()
        : typeof attachment.id === 'string'
          ? attachment.id.trim()
          : ''
    const filename = typeof attachment.name === 'string' ? attachment.name.trim() : ''
    if (!inputRef || !filename) {
      return []
    }

    const sizeBytes =
      typeof attachmentWithMetadata.sizeBytes === 'number' &&
      Number.isFinite(attachmentWithMetadata.sizeBytes) &&
      attachmentWithMetadata.sizeBytes >= 0
        ? attachmentWithMetadata.sizeBytes
        : attachment.file?.size
    const kind = typeof attachmentWithMetadata.kind === 'string' ? attachmentWithMetadata.kind.trim() : ''

    return [
      {
        inputRef,
        filename,
        ...(attachment.contentType ? { contentType: attachment.contentType } : {}),
        ...(kind ? { kind } : {}),
        ...(attachment.file ? { file: attachment.file } : {}),
        ...(sizeBytes != null ? { sizeBytes } : {}),
      },
    ]
  })
}

async function sendCancellation(connection: HubConnection, turn: DmpChatTurn): Promise<void> {
  await connection.invoke('SendMessageToChat', turn.chatId, createDmpChatCancellationMessage(turn.requestId))
}
